import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const EMPTY = {
  name: "",
  tagline: "",
  description: "",
  category: "Purificazione",
  image: "",
  specs: [],
  features: [],
  order: 0,
  active: true,
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [specsText, setSpecsText] = useState("");
  const [featuresText, setFeaturesText] = useState("");

  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new"); setForm(EMPTY); setSpecsText(""); setFeaturesText("");
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p });
    setSpecsText((p.specs ?? []).map((s) => `${s.label}: ${s.value}`).join("\n"));
    setFeaturesText((p.features ?? []).join("\n"));
  };
  const close = () => { setEditing(null); setForm(EMPTY); setSpecsText(""); setFeaturesText(""); };
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const save = async (e) => {
    e.preventDefault();
    const specs = specsText.split("\n").map((l) => l.split(":")).filter((p) => p.length >= 2)
      .map(([label, ...rest]) => ({ label: label.trim(), value: rest.join(":").trim() }));
    const features = featuresText.split("\n").map((l) => l.trim()).filter(Boolean);
    const payload = { ...form, order: Number(form.order) || 0, specs, features };
    try {
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing}`, payload);
      toast.success("Salvato.");
      close(); load();
    } catch (err) {
      toast.error(apiError(err) ?? "Errore.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il prodotto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Eliminato.");
    load();
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-products">
      <div className="flex items-baseline justify-between mb-12">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-2">— PRODOTTI</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Catalogo</h1>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="admin-product-new"><Plus size={14} /> Nuovo prodotto</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((p) => (
          <div key={p.id} className="border border-[#e4e4e7] bg-[#f4f4f5]" data-testid={`admin-product-${p.name}`}>
            {p.image && (
              <div className="aspect-[4/3] overflow-hidden">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-6">
              <div className="font-mono text-[10px] tracking-widest text-[#3f3f46] uppercase mb-2">{p.category}</div>
              <div className="font-display text-2xl tracking-tighter">{p.name}</div>
              <div className="text-sm text-[#3f3f46] mt-2">{p.tagline}</div>
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#e4e4e7]">
                <span className={`font-mono text-[10px] tracking-widest ${p.active ? "text-[#007A8A]" : "text-[#52525b]"}`}>
                  {p.active ? "● ATTIVO" : "○ NASCOSTO"} · ORDINE {p.order}
                </span>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="p-2 hover:text-[#007A8A]" data-testid={`edit-product-${p.name}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(p.id)} className="p-2 hover:text-[#FF4D00]" data-testid={`delete-product-${p.name}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-6" data-testid="admin-product-editor">
          <form onSubmit={save} className="w-full max-w-3xl bg-[#f4f4f5] border border-[#e4e4e7] my-12">
            <div className="flex items-center justify-between p-6 border-b border-[#e4e4e7]">
              <div className="font-display text-2xl">{editing === "new" ? "Nuovo prodotto" : "Modifica prodotto"}</div>
              <button type="button" onClick={close}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Nome" required><input required value={form.name} onChange={set("name")} className="input-admin" /></Field>
                <Field label="Categoria"><input value={form.category} onChange={set("category")} className="input-admin" /></Field>
              </div>
              <Field label="Tagline"><input value={form.tagline} onChange={set("tagline")} className="input-admin" /></Field>
              <Field label="Descrizione"><textarea rows={3} value={form.description} onChange={set("description")} className="input-admin resize-none" /></Field>
              <Field label="URL immagine"><input value={form.image ?? ""} onChange={set("image")} className="input-admin" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Ordine"><input type="number" value={form.order} onChange={set("order")} className="input-admin" /></Field>
                <label className="flex items-center gap-3 cursor-pointer pt-6">
                  <input type="checkbox" checked={form.active} onChange={set("active")} />
                  <span className="font-mono text-[10px] tracking-widest uppercase">Attivo</span>
                </label>
              </div>
              <Field label="Specifiche (una per riga: Label: Valore)">
                <textarea rows={4} value={specsText} onChange={(e) => setSpecsText(e.target.value)} className="input-admin resize-none font-mono text-xs" />
              </Field>
              <Field label="Feature (una per riga)">
                <textarea rows={4} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} className="input-admin resize-none font-mono text-xs" />
              </Field>
            </div>
            <div className="p-6 border-t border-[#e4e4e7] flex justify-end gap-3">
              <button type="button" onClick={close} className="btn-ghost">Annulla</button>
              <button type="submit" className="btn-primary">Salva</button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .input-admin { width:100%; background:transparent; border:1px solid #d4d4d8; padding:0.7rem 0.9rem; color:#0a0a0a; outline:none; font-family:'IBM Plex Sans',sans-serif; transition:border-color .2s; }
        .input-admin:focus { border-color:#007A8A; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#3f3f46] mb-2 inline-block">
        {label} {required && <span className="text-[#FF4D00]">*</span>}
      </span>
      {children}
    </label>
  );
}
